import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SelectedSiteService } from '../../selected-site.service';
import { StagingService, StagingStatus } from '../../core/staging';
import { Site, SiteService } from '../../core/site';
import { Job } from '../../core/job';
import { ApiErrorFactory } from '../../core/api-errors';
import { WordPressInstance } from '../../core/debug-site';
import { ErrorDialog } from '../dialog';
import { SiteEditService } from '../../core/site-edit';
import { AccountsService } from '../../accounts.service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

@Component({
  selector: 'app-staging-page',
  templateUrl: './staging.component.html',
  styleUrls: ['./staging.component.scss'],
  standalone: false,
})
export class StagingComponent implements OnInit {
  site$: Observable<Site>;
  siteId$: Observable<string>;
  editUrl$: Observable<string>;
  stagingStatus$: Observable<StagingStatus>;
  lastJobInfo$: Observable<Job>;
  errorDialogRef: MatDialogRef<ErrorDialog>;
  isExpress$: Observable<boolean>;
  readonly instance: WordPressInstance = WordPressInstance.staging;

  constructor(
    private selectedSiteService: SelectedSiteService,
    private stagingService: StagingService,
    private siteService: SiteService,
    private matDialog: MatDialog,
    private apiErrorFactory: ApiErrorFactory,
    private siteEditService: SiteEditService,
    private accountsService: AccountsService,
    public readonly analyticsService: ProductAnalyticsService,
  ) {
    this.isExpress$ = this.accountsService.isExpress$;
    this.siteId$ = this.selectedSiteService.getSiteId();
  }

  ngOnInit(): void {
    this.site$ = this.siteService.get();
    this.stagingStatus$ = this.stagingService.getStagingStatus();
    this.lastJobInfo$ = this.stagingService.getLastJobInfo();
    this.editUrl$ = this.siteEditService.getStagingEditUrl();
    this.setupErrorHandling();
  }

  sendCreateRequest(): void {
    this.analyticsService.trackEvent('create-staging:create-request', 'create-staging', 'create-request');
    this.stagingService.createStagingRequest();
  }

  sendResetRequest(): void {
    this.stagingService.resetStagingRequest();
  }

  sendResetFromTemplateRequest(templateId: string): void {
    this.analyticsService.trackEvent(
      'reset-staging-from-template',
      'reset-staging-from-template',
      'reset-from-template-request',
    );
    this.stagingService.resetStagingFromTemplateRequest(templateId);
  }

  /**
   * Responsible for sending staging push requests to the service once pushLiveRequest emits an event.
   * @param options - PushLiveOptions (typed as `any` because of $event.valueOf() being passed in)
   */
  sendPushLiveRequest(options: any): void {
    this.analyticsService.trackEvent('staging:push-to-live-request', 'staging-push-to-live', 'push-to-live-request');
    this.stagingService.pushStagingRequest(options);
  }

  private setupErrorHandling(): void {
    this.stagingService
      .getErrors()
      .pipe(filter((err: HttpErrorResponse) => !!err))
      .subscribe((err: HttpErrorResponse) => this.handleError(err));
  }

  private handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.errorDialogRef = this.matDialog.open(ErrorDialog, {
      data: { errMsg: parsedError.getUserMessage() },
    });
  }
}
